import {
  Box,
  Divider,
  Grid,
  Paper,
  Typography,
  Checkbox,
  FormControlLabel,
  Button,
  InputAdornment,
  TextField,
  useMediaQuery,
  useTheme,
  MenuItem,
  Tooltip,
  IconButton,
} from "@mui/material";
import { Autocomplete } from "@mui/material";
import GCAutocompleteCheckboxFixSizeUid from "../VendorDiscontinueChangeRequest/GCAutocompleteCheckboxFixSizeUid";
import { useSelector } from "react-redux";
import moment from "moment";
import { useState } from "react";
import { CmpDatePickerField001V1 } from "../../Component/Common/ToolBox/ToolBoxV1";
import { fExecuteAPI } from "../../Server/Apis";
import SearchIcon from "@mui/icons-material/Search";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import CloseIcon from "@mui/icons-material/Close";
import dayjs from "dayjs";

const GeneralLedger = () => {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const branchData = useSelector((state) => state.SliceDB.value.DbData.Branch);
  const kernOBj = useSelector((state) => state.SliceDB.value.DBKernObj);

  const [filterObj, setFilterObj] = useState({
    branch: [],
    fromDate: dayjs().startOf("year"),
    toDate: dayjs(),
    addPDC: false,
    excludeYearEndJV: false,
    excludeSpecialJV: false,
    accountName: "",
    accountOptions: [],
  });

  const [showFilters, setShowFilters] = useState(true);
  const [reportUrl, setReportUrl] = useState(null);

  const handleFilterChange = (key, value) => {
    setFilterObj((prev) => ({ ...prev, [key]: value }));
  };

  const handleReset = () => {
    setFilterObj({
      branch: [],
      fromDate: dayjs().startOf("year"),
      toDate: dayjs(),
      addPDC: false,
      excludeYearEndJV: false,
      excludeSpecialJV: false,
      accountName: "",
      accountOptions: [],
    });
    setReportUrl(null);
  };

  const handleViewReport = async () => {
    try {
      const acCD = filterObj.accountOptions.find(
        (account) => account.Name1 === filterObj.accountName
      );
      const URL = kernOBj?.WEB_REPORT_BASE_URL?.Val;
      let prntyp = "";

      if (!filterObj.addPDC) {
        prntyp = acCD?.AcCd === "All" ? "GLNOPDC_RNG" : "GLNOPDC";
      } else {
        prntyp = acCD?.AcCd === "All" ? "GLPDC_RNG" : "GLPDC";
      }

      const from = filterObj.fromDate?.format("MM/DD/YYYY"); // or MM/DD/YYYY if backend needs it
      const to = filterObj.toDate?.format("MM/DD/YYYY"); // be consistent with backend

      const url =
        `${URL}WMS/ACCLDGRReport?CCELocations=${filterObj.branch.join(",")}` +
        `&sluGlAccount=${acCD?.AcCd || "ALL"}&txtFromDt=${from}&txtToDt=${to}` +
        `&chkyrEndJV=${filterObj.excludeYearEndJV}&chkSplJV=${filterObj.excludeSpecialJV}` +
        `&chkPDC=${filterObj.addPDC}&prntyp=${prntyp}`;
      setReportUrl(url);
      setShowFilters(false);
    } catch (error) {
      console.error("Error constructing report URL:", error);
    }
  };

  const getAccountData = async (searchText) => {
    try {
      const payload = {
        Loc: "",
        Data: "%" + (searchText || "All"),
        Type: "Any",
        AcTyp: "G",
        Exact: "N",
      };
      const res = await fExecuteAPI("AccountTransSearch_GETROW", payload);
      if (res.Type === "SUCCESS") {
        setFilterObj((prev) => ({
          ...prev,
          accountOptions: res.ResultSet,
        }));
      }
    } catch (error) {
      console.error("Error fetching account data:", error);
    }
  };

  return (
    <Box sx={{ width: "100%", p: 1 }}>
      <Tooltip title={showFilters ? "Hide Filters" : "Show Filters"}>
        <IconButton
          onClick={() => setShowFilters((prev) => !prev)}
          sx={{ mb: 1 }}
          color="primary"
        >
          {showFilters ? <CloseIcon /> : <FilterAltIcon />}
        </IconButton>
      </Tooltip>

      <Grid container spacing={1} alignItems="flex-start">
        {showFilters && (
          <Grid item xs={12} md={3}>
            <Paper
              elevation={3}
              sx={{ p: 2, height: "100%", overflowY: "auto" }}
            >
              <Typography variant="h6" gutterBottom>
                Filters
              </Typography>

              <Typography variant="subtitle2">Branch</Typography>
              <GCAutocompleteCheckboxFixSizeUid
                xLabelText="Branch"
                xValue={filterObj.branch}
                setValue={(val) => handleFilterChange("branch", val)}
                options={branchData}
                objName="branch"
                placeholder="Search Branch"
              />

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2">A/c Name</Typography>
              <Autocomplete
                fullWidth
                value={filterObj.accountName}
                onChange={(e, val) => handleFilterChange("accountName", val)}
                inputValue={filterObj.accountName ?? ""}
                onInputChange={(e, val) =>
                  handleFilterChange("accountName", val)
                }
                options={filterObj.accountOptions.map((a) => a.Name1)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search Account"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        getAccountData(filterObj.accountName);
                      }
                    }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {params.InputProps.endAdornment}
                          <InputAdornment
                            position="end"
                            sx={{
                              position: "absolute",
                              right: 45,
                              top: "50%",
                              transform: "translateY(-50%)",
                              cursor: "pointer",
                            }}
                          >
                            <Tooltip title="Search Account">
                              <SearchIcon
                                onClick={() =>
                                  getAccountData(filterObj.accountName)
                                }
                              />
                            </Tooltip>
                          </InputAdornment>
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <MenuItem {...props} key={option}>
                    {option}
                  </MenuItem>
                )}
              />

              <Divider sx={{ my: 2 }} />

              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Typography variant="body2">From</Typography>
                  <CmpDatePickerField001V1
                    xName="fromDate"
                    xLabelText=""
                    xValue={filterObj.fromDate}
                    xOnChange={(val) => handleFilterChange("fromDate", val)}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">To</Typography>
                  <CmpDatePickerField001V1
                    xName="toDate"
                    xLabelText=""
                    xValue={filterObj.toDate}
                    xOnChange={(val) => handleFilterChange("toDate", val)}
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={filterObj.addPDC}
                    onChange={(e) =>
                      handleFilterChange("addPDC", e.target.checked)
                    }
                  />
                }
                label={
                  <Tooltip title="Include Post Dated Cheques">
                    <span>Add PDC</span>
                  </Tooltip>
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filterObj.excludeYearEndJV}
                    onChange={(e) =>
                      handleFilterChange("excludeYearEndJV", e.target.checked)
                    }
                  />
                }
                label={
                  <Tooltip title="Exclude year-end journal vouchers">
                    <span>Exclude Year End JV</span>
                  </Tooltip>
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filterObj.excludeSpecialJV}
                    onChange={(e) =>
                      handleFilterChange("excludeSpecialJV", e.target.checked)
                    }
                  />
                }
                label={
                  <Tooltip title="Exclude special journal vouchers">
                    <span>Exclude Special JV</span>
                  </Tooltip>
                }
              />

              <Divider sx={{ my: 2 }} />

              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                fullWidth
                onClick={handleViewReport}
              >
                View Report
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<RestartAltIcon />}
                fullWidth
                onClick={handleReset}
                sx={{ mt: 1 }}
              >
                Reset
              </Button>
            </Paper>
          </Grid>
        )}

        <Grid item xs={12} md={showFilters ? 9 : 12}>
          <Paper
            elevation={3}
            sx={{
              p: 2,
              height: isMdUp ? "calc(100vh - 130px)" : "auto",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <Typography variant="h6" gutterBottom>
              Ledger Report
            </Typography>

            {reportUrl ? (
              <iframe
                src={reportUrl}
                title="General Ledger Report"
                style={{
                  width: "100%",
                  height: "100%",
                  border: "none",
                  overflow: "hidden", // ✅ Final fix: no extra scroll from iframe
                  display: "block", // ✅ Prevents inline white space
                }}
              />
            ) : (
              <Box
                sx={{
                  border: "1px dashed grey",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "text.secondary",
                }}
              >
                Report preview area
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default GeneralLedger;
